import { Add, Delete, Edit } from "@mui/icons-material";
import BlindsClosedIcon from "@mui/icons-material/BlindsClosed";
import {
  Grid,
  IconButton,
  Skeleton,
  Stack,
  TableCell,
  Tooltip,
  Typography,
} from "@mui/material";
import EmptyContent from "components/EmptyContent";
import List from "components/UI/List";
import { useAlert } from "contexts/alertContext";
import { Feature } from "models/api";
import {
  IWorkshopEvaluationQuestionOption,
  IWorkshopEvaluationQuestionOptionInput,
} from "models/workshopEvaluationQuestionOption";
import React, { useCallback, useEffect, useState } from "react";
import { QueryClient, useMutation } from "react-query";

import {
  WorkshopEvaluationQuestionOptionCells,
  addWorkshopEvaluationQuestionOption,
  deleteWorkshopEvaluationQuestionOption,
  editWorkshopEvaluationQuestionOption,
  getWorkshopEvaluationQuestionOptions,
} from "framework/workshopEvaluationQuestionOption";
import AddWorkshopEvaluationQuestionOption from "./partials/AddWorkshopEvaluationQuestionOption/AddWorkshopEvaluationQuestionOption";
import BlockIcon from "@mui/icons-material/Block";
import EditWorkshopEvaluationQuestionOption from "./partials/EditWorkshopEvaluationQuestionOption/EditWorkshopEvaluationQuestionOption";
import DeleteWorkshopEvaluationQuestionOption from "./partials/DeleteWorkshopEvaluationQuestionOption/DeleteWorkshopEvaluationQuestionOption";
import { handleError } from "helpers/utils";
import { useTranslation } from "react-i18next";
import { IPermission } from "models/permissions";
import { getPermission } from "helpers";
import { useParams } from "react-router-dom";

interface Props {
  feature: Feature;
}

const WorkshopEvaluationQuestionOptions: React.FC<Props> = ({ feature }) => {
  const urlParams = useParams();
  let questionId: string = urlParams[`${feature.singleName}Id`] as string;
  const [loading, setLoading] = useState<boolean>(true);
  const [loading3, setLoading3] = useState<boolean>(true);
  const { t, i18n } = useTranslation();
  const [permission, setPermission] = useState<IPermission | null>(null);
  const [loading2, setLoading2] = useState<boolean>(false);
  const [WorkshopEvaluationQuestion, setWorkshopEvaluationQuestionOption] =
    useState<IWorkshopEvaluationQuestionOption[]>(
      [] as IWorkshopEvaluationQuestionOption[]
    );
  const [
    FilterWorkshopEvaluationQuestion,
    setFilterWorkshopEvaluationQuestionOption,
  ] = useState<IWorkshopEvaluationQuestionOption[]>(
    [] as IWorkshopEvaluationQuestionOption[]
  );
  const [openAdd, setOpenAdd] = useState<boolean>(false);
  const [openEdit, setOpenEdit] = useState<boolean>(false);
  const [openDelete, setOpenDelete] = useState<boolean>(false);
  const [
    selectedWorkshopEvaluationQuestionOption,
    setSelectedWorkshopEvaluationQuestionOption,
  ] = useState<IWorkshopEvaluationQuestionOption>(
    {} as IWorkshopEvaluationQuestionOption
  );
  const [inputObj, setInputObj] =
    useState<IWorkshopEvaluationQuestionOptionInput>(
      {} as IWorkshopEvaluationQuestionOptionInput
    );
  const { setAlert } = useAlert();

  // eslint-disable-next-line
  const queryClient = new QueryClient();

  const addWorkshopEvaluationQuestionMutation = useMutation({
    mutationFn: (createInput: IWorkshopEvaluationQuestionOptionInput) => {
      return addWorkshopEvaluationQuestionOption(createInput);
    },
  });

  const editWorkshopEvaluationQuestionMutation = useMutation({
    mutationFn: (data: {
      createInput: IWorkshopEvaluationQuestionOptionInput;
      id: string;
    }) => {
      return editWorkshopEvaluationQuestionOption(data.createInput, data.id);
    },
  });

  const deleteWorkshopEvaluationQuestionOptionMutation = useMutation({
    mutationFn: (data: { id: string }) => {
      return deleteWorkshopEvaluationQuestionOption(data.id);
    },
  });

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      let response: any = await queryClient.fetchQuery(
        "get workshop evaluation question options",
        () =>
          getWorkshopEvaluationQuestionOptions(
            `/byquestion?workshopEvaluationQuestionId=${questionId}`
          )
      );
      response =
        response && response.length > 0
          ? response.map((item: IWorkshopEvaluationQuestionOption) => ({
            ...item,
          })).sort((a: IWorkshopEvaluationQuestionOption, b: IWorkshopEvaluationQuestionOption) => a.optionOrder - b.optionOrder)
          : [];
      setWorkshopEvaluationQuestionOption(response);
      setFilterWorkshopEvaluationQuestionOption(response);
      setLoading(false);
    } catch (error: any) {
      setAlert({
        open: true,
        message: handleError(error) || "Something went wrong",
        type: "error",
      });
      setLoading(false);
    }

    // eslint-disable-next-line
  }, [queryClient, setAlert]);

  const fetchPermission = useCallback(async () => {
    setLoading3(true);
    try {
      const res = await getPermission(2);
      setPermission(res);
      setLoading3(false);
    } catch (error: any) {
      setAlert({
        open: true,
        message: handleError(error) || "Something went wrong",
        type: "error",
      });
      setLoading3(false);
    }

    // eslint-disable-next-line
  }, [queryClient, setAlert]);

  useEffect(() => {
    fetchData();
    fetchPermission();
    // eslint-disable-next-line
  }, []);

  if (loading || loading3)
    return (
      <Stack spacing={2} p={2}>
        {Array.from({ length: 10 }).map((_, i) => (
          <Skeleton variant="rectangular" key={i} width="100%" height={30} />
        ))}
      </Stack>
    );

  const handleChange = (name: string, value: any) => {
    if (name === "score" || name === "optionOrder") {
      value = parseInt(value);
    }
    setInputObj((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleOpenAdd = () => setOpenAdd(true);
  const handleCloseAdd = () => setOpenAdd(false);

  const handleOpenEdit = (
    WorkshopEvaluationQuestion: IWorkshopEvaluationQuestionOption
  ) => {
    setSelectedWorkshopEvaluationQuestionOption(WorkshopEvaluationQuestion);
    setOpenEdit(true);
  };
  const handleCloseEdit = () => {
    setOpenEdit(false);
    setSelectedWorkshopEvaluationQuestionOption(
      {} as IWorkshopEvaluationQuestionOption
    );
  };

  const handleOpenDelete = (
    WorkshopEvaluationQuestion: IWorkshopEvaluationQuestionOption
  ) => {
    setSelectedWorkshopEvaluationQuestionOption(WorkshopEvaluationQuestion);
    setOpenDelete(true);
  };

  const handleCloseDelete = () => {
    setOpenDelete(false);
    setSelectedWorkshopEvaluationQuestionOption(
      {} as IWorkshopEvaluationQuestionOption
    );
  };

  const getBodyCells = (row: IWorkshopEvaluationQuestionOption) => {
    let cells = Object.keys(row).map((key: string, i: number) => {
      if (key === "description")
        return {
          id: "description",
          element: (
            <TableCell align={"left"} key={i}>
              <Typography variant="body2">{row.description}</Typography>
            </TableCell>
          ),
        };

      if (key === "descriptionAr")
        return {
          id: "descriptionAr",
          element: (
            <TableCell align={"left"} key={i}>
              <Typography variant="body2">{row.descriptionAr}</Typography>
            </TableCell>
          ),
        };

      if (key === "score")
        return {
          id: "score",
          element: (
            <TableCell align={"left"} key={i}>
              <Typography variant="body2">{row.score}</Typography>
            </TableCell>
          ),
        };

      if (key === "optionOrder")
        return {
          id: "optionOrder",
          element: (
            <TableCell align={"left"} key={i}>
              <Typography variant="body2">{row.optionOrder}</Typography>
            </TableCell>
          ),
        };

      if (key === "id")
        return {
          id: "actions",
          element: (
            <TableCell align={"right"} key={i}>
              <Stack
                direction={"row"}
                alignContent={"center"}
                justifyContent={i18n.language === "en" ? "start" : "end"}
                spacing={1}
              >
                {permission?.hasModifyPermission && (
                  <Tooltip title={t("T_edit")}>
                    <IconButton
                      size={"small"}
                      color={"primary"}
                      onClick={() => handleOpenEdit(row)}
                    >
                      <Edit />
                    </IconButton>
                  </Tooltip>
                )}

                {permission?.hasDeletePermission && (
                  <Tooltip title={t("T_delete")}>
                    <IconButton
                      size={"small"}
                      color={"error"}
                      onClick={() => handleOpenDelete(row)}
                    >
                      <Delete />
                    </IconButton>
                  </Tooltip>
                )}
              </Stack>
            </TableCell>
          ),
        };
    });
    return cells;
  };

  const handleAdd = async () => {
    if (!inputObj.description || !inputObj.descriptionAr || !inputObj.score || !inputObj.optionOrder)
      return setAlert({
        open: true,
        message: "Please fill all fields",
        type: "error",
      });
    try {
      setLoading2(true);
      inputObj.workshopEvaluationQuestionId = questionId;
      await addWorkshopEvaluationQuestionMutation.mutateAsync(inputObj);
      setAlert({
        open: true,
        message: "WorkshopEvaluationQuestion added successfully",
        type: "success",
      });
      fetchData();
      handleCloseAdd();
      setInputObj({} as IWorkshopEvaluationQuestionOptionInput);
      setLoading2(false);
    } catch (error: any) {
      setAlert({
        open: true,
        message: handleError(error) || "Something went wrong",
        type: "error",
      });
      setLoading2(false);
    }
  };

  const handleEdit = async () => {
    try {
      setLoading2(true);
      let createInput: IWorkshopEvaluationQuestionOptionInput = {
        workshopEvaluationQuestionId: questionId,
        description:
          inputObj.description ||
          selectedWorkshopEvaluationQuestionOption.description,
        descriptionAr:
          inputObj.descriptionAr ||
          selectedWorkshopEvaluationQuestionOption.descriptionAr,
        score: inputObj.score || selectedWorkshopEvaluationQuestionOption.score,
        optionOrder: inputObj.optionOrder || selectedWorkshopEvaluationQuestionOption.optionOrder,
      };
      await editWorkshopEvaluationQuestionMutation.mutateAsync({
        createInput,
        id: selectedWorkshopEvaluationQuestionOption.id,
      });
      setAlert({
        open: true,
        message: "WorkshopEvaluationQuestion updated successfully",
        type: "success",
      });
      fetchData();
      handleCloseEdit();
      setInputObj({} as IWorkshopEvaluationQuestionOptionInput);
      setLoading2(false);
    } catch (error: any) {
      setAlert({
        open: true,
        message: handleError(error) || "Something went wrong",
        type: "error",
      });
      setLoading2(false);
    }
  };

  const handleDelete = async () => {
    try {
      setLoading2(true);
      await deleteWorkshopEvaluationQuestionOptionMutation.mutateAsync({
        id: selectedWorkshopEvaluationQuestionOption.id,
      });
      setAlert({
        open: true,
        message: "WorkshopEvaluationQuestion deleted successfully",
        type: "success",
      });
      fetchData();
      handleCloseDelete();
      setLoading2(false);
    } catch (error: any) {
      setAlert({
        open: true,
        message: handleError(error) || "Something went wrong",
        type: "error",
      });
      setLoading2(false);
    }
  };

  return (
    <>
      <Stack spacing={2} p={2}>
        <Grid
          container
          justifyContent={"space-between"}
          alignItems="center"
          sx={{ mb: 4 }}
        >
          <Typography variant="h3" color={"header.primary"}>
            {t(feature.name)}
          </Typography>
          <Stack direction={"row"} spacing={1}>
            {permission?.hasModifyPermission && (
              <Tooltip title={t("T_add")}>
                <IconButton
                  sx={{
                    // bgcolor: 'primary.main',
                    color: "primary.main",
                    "&:hover": {
                      bgcolor: "primary.light",
                      color: "white",
                    },
                    height: 40,
                    width: 40,
                    transition: "0.5s",
                  }}
                  onClick={handleOpenAdd}
                >
                  <Add />
                </IconButton>
              </Tooltip>
            )}
          </Stack>
        </Grid>

        {permission?.hasReadPermission! &&
          FilterWorkshopEvaluationQuestion.length > 0 && (
            <List
              headCells={WorkshopEvaluationQuestionOptionCells}
              data={FilterWorkshopEvaluationQuestion}
              getBodyCells={getBodyCells}
              feature={feature}
              canEdit={false}
            />
          )}
        {(!FilterWorkshopEvaluationQuestion ||
          FilterWorkshopEvaluationQuestion.length === 0) &&
          permission?.hasReadPermission! && (
            <EmptyContent
              icon={<BlindsClosedIcon sx={{ fontSize: "3.5rem " }} />}
              header={t("T_no_option_header")}
              message={t("T_no_option_message")}
            />
          )}
        {!permission?.hasReadPermission && (
          <EmptyContent
            icon={<BlockIcon sx={{ fontSize: "3.5rem " }} />}
            header={t("T_no_permissions")}
            message={t("T_no_permissions_message")}
          />
        )}
      </Stack>

      {openAdd && (
        <AddWorkshopEvaluationQuestionOption
          open={openAdd}
          loading={loading2}
          onClose={handleCloseAdd}
          onSubmit={handleAdd}
          onChanges={handleChange}
        />
      )}
      {openEdit && (
        <EditWorkshopEvaluationQuestionOption
          open={openEdit}
          loading={loading2}
          workshopEvaluationQuestionOption={
            selectedWorkshopEvaluationQuestionOption
          }
          onClose={handleCloseEdit}
          onSubmit={handleEdit}
          onChanges={handleChange}
        />
      )}
      {openDelete && (
        <DeleteWorkshopEvaluationQuestionOption
          open={openDelete}
          loading={loading2}
          onClose={handleCloseDelete}
          onSubmit={handleDelete}
        />
      )}
    </>
  );
};

export default WorkshopEvaluationQuestionOptions;
